import React, { useState, useRef } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import Container from '../components/Container';
import SectionHeader from '../components/SectionHeader';
import Button from '../components/Button';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import '../styles/components.css';

const IMG_CONTACT = "/images/contact.jpg";

const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    siret: '',
    message: '',
    consentement: false,
    honeypot: '' // Anti-spam
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const formRef = useRef(null);
  const infoRef = useRef(null);

  const formInView = useInView(formRef, { once: true, margin: "-100px" });
  const infoInView = useInView(infoRef, { once: true, margin: "-100px" });

  const formControls = useAnimation();
  const infoControls = useAnimation();

  React.useEffect(() => {
    if (formInView) formControls.start("visible");
  }, [formInView, formControls]);

  React.useEffect(() => {
    if (infoInView) infoControls.start("visible");
  }, [infoInView, infoControls]);

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!formData.sujet) {
      newErrors.sujet = 'Veuillez sélectionner un sujet';
    }

    // SIRET obligatoire pour les professionnels (14 chiffres)
    if (formData.sujet === 'professionnels') {
      const siretDigits = (formData.siret || '').replace(/\D/g, '');
      if (!siretDigits) {
        newErrors.siret = 'Le numéro SIRET est requis pour les professionnels';
      } else if (siretDigits.length !== 14) {
        newErrors.siret = 'Le SIRET doit contenir exactement 14 chiffres';
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }
    
    // Honeypot check
    if (formData.honeypot) {
      newErrors.honeypot = 'Spam détecté';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // En développement, utiliser le backend local par défaut pour enregistrer en base
    const apiUrl = process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');
    const formId = process.env.REACT_APP_FORMSPREE_FORM_ID;
    if (!apiUrl && !formId) {
      console.warn('Contact: REACT_APP_FORMSPREE_FORM_ID ou REACT_APP_API_URL non défini. Définir dans .env puis rebuild.');
    }
    const payload = {
      nom: formData.nom,
      email: formData.email,
      telephone: formData.telephone,
      sujet: formData.sujet,
      siret: formData.siret || undefined,
      message: formData.message
    };
    
    try {
      if (apiUrl) {
        // Backend : enregistre en base + envoie l'email via Formspree
        const response = await fetch(`${apiUrl.replace(/\/$/, '')}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `Erreur ${response.status}`);
      } else if (formId) {
        // Envoi direct Formspree (sans backend)
        const response = await fetch(`https://formspree.io/f/${formId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Erreur ${response.status}`);
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      setSubmitStatus('success');
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        sujet: '',
        siret: '',
        message: '',
        consentement: false,
        honeypot: ''
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="section" style={{ overflow: 'hidden' }}>
      {/* Hero : image en arrière-plan, titre par-dessus */}
      <div
        role="img"
        aria-label="Contact"
        style={{
          position: 'relative',
          width: '100%',
          height: '50vh',
          minHeight: 280,
          backgroundImage: `url(${IMG_CONTACT})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 0
          }}
        />
        <Container style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center' }}
          >
            <SectionHeader
              className="mission-hero-header"
              title="Contactez-nous"
              subtitle="Nous sommes à votre écoute pour répondre à toutes vos questions et vous accompagner dans vos projets."
            />
          </motion.div>
        </Container>
      </div>

      <Container>
        <Row className="g-4" style={{ marginTop: 'var(--spacing-3xl)' }}>
          {/* Formulaire */}
          <Col lg={7}>
            <motion.div
              ref={formRef}
              initial="hidden"
              animate={formControls}
              variants={fadeInLeft}
            >
              <div className="card-modern" style={{
                background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.02) 0%, rgba(2, 132, 199, 0.02) 100%)'
              }}>
              <h3 style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text)' }}>
                Envoyez-nous un message
              </h3>
              
              {submitStatus === 'success' && (
                <Alert variant="success" className="mb-4">
                  <strong>Message envoyé avec succès !</strong> Nous vous répondrons dans les plus brefs délais.
                </Alert>
              )}
              
              {submitStatus === 'error' && (
                <Alert variant="danger" className="mb-4">
                  <strong>Erreur lors de l'envoi.</strong> Veuillez réessayer ou nous contacter directement par téléphone.
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                {/* Honeypot field (invisible) */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                <div className="form-group-modern">
                  <label htmlFor="nom" className="form-label-modern">
                    Nom complet <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={`form-control-modern ${errors.nom ? 'is-invalid' : ''}`}
                    placeholder="Votre nom"
                    required
                  />
                  {errors.nom && <div className="form-error">{errors.nom}</div>}
                </div>

                <div className="form-group-modern">
                  <label htmlFor="email" className="form-label-modern">
                    Email <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control-modern ${errors.email ? 'is-invalid' : ''}`}
                    placeholder="votre.email@exemple.com"
                    required
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>

                <div className="form-group-modern">
                  <label htmlFor="telephone" className="form-label-modern">
                    Téléphone <span style={{ color: 'var(--color-text-muted)' }}>(optionnel)</span>
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="form-control-modern"
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <div className="form-group-modern">
                  <label htmlFor="sujet" className="form-label-modern">
                    Sujet <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <select
                    id="sujet"
                    name="sujet"
                    value={formData.sujet}
                    onChange={handleChange}
                    className={`form-control-modern ${errors.sujet ? 'is-invalid' : ''}`}
                    required
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="particuliers">Particuliers</option>
                    <option value="professionnels">Professionnels</option>
                    <option value="collectivites">Collectivités</option>
                    <option value="autre">Autre</option>
                  </select>
                  {errors.sujet && <div className="form-error">{errors.sujet}</div>}
                </div>

                {formData.sujet === 'professionnels' && (
                  <div className="form-group-modern">
                    <label htmlFor="siret" className="form-label-modern">
                      Numéro SIRET <span style={{ color: 'var(--color-error)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      id="siret"
                      name="siret"
                      value={formData.siret}
                      onChange={handleChange}
                      className={`form-control-modern ${errors.siret ? 'is-invalid' : ''}`}
                      placeholder="14 chiffres (ex: 123 456 789 00012)"
                      maxLength={17}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                    {errors.siret && <div className="form-error">{errors.siret}</div>}
                    <small style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                      SIRET = 14 chiffres (espaces autorisés)
                    </small>
                  </div>
                )}

                <div className="form-group-modern">
                  <label htmlFor="message" className="form-label-modern">
                    Message <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`form-control-modern ${errors.message ? 'is-invalid' : ''}`}
                    placeholder="Décrivez votre demande..."
                    rows="6"
                    required
                  />
                  {errors.message && <div className="form-error">{errors.message}</div>}
                </div>

                <div className="form-group-modern">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="consentement"
                      checked={formData.consentement}
                      onChange={handleChange}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-light)' }}>
                      J'accepte que mes données soient utilisées pour me recontacter
                    </span>
                  </label>
                </div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isSubmitting}
                    style={{ width: '100%', marginTop: 'var(--spacing-md)' }}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </Button>
                </motion.div>
              </form>
              </div>
            </motion.div>
          </Col>

          {/* Informations de contact */}
          <Col lg={5}>
            <motion.div
              ref={infoRef}
              initial="hidden"
              animate={infoControls}
              variants={fadeInRight}
            >
              <div className="card-modern" style={{ 
                height: '100%',
                background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.02) 0%, rgba(2, 132, 199, 0.02) 100%)'
              }}>
              <h3 style={{ marginBottom: 'var(--spacing-xl)', color: 'var(--color-text)' }}>
                Nos coordonnées
              </h3>

              <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 'var(--spacing-lg)' }}>
                  <MapPin size={24} style={{ color: 'var(--color-primary)', marginRight: 'var(--spacing-md)', flexShrink: 0, marginTop: '0.25rem' }} />
                  <div>
                    <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text)' }}>
                      Adresse
                    </strong>
                    <p style={{ margin: 0, color: 'var(--color-text-light)', lineHeight: 'var(--line-height-relaxed)' }}>
                      9 bis rue Gallieni<br />
                      France
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 'var(--spacing-lg)' }}>
                  <Phone size={24} style={{ color: 'var(--color-primary)', marginRight: 'var(--spacing-md)', flexShrink: 0, marginTop: '0.25rem' }} />
                  <div>
                    <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text)' }}>
                      Téléphone
                    </strong>
                    <p style={{ margin: 0, color: 'var(--color-text-light)' }}>
                      <a href="tel:+33612096424" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
                        +33 6 12 09 64 24
                      </a>
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 'var(--spacing-lg)' }}>
                  <Mail size={24} style={{ color: 'var(--color-primary)', marginRight: 'var(--spacing-md)', flexShrink: 0, marginTop: '0.25rem' }} />
                  <div>
                    <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text)' }}>
                      Email
                    </strong>
                    <p style={{ margin: 0, color: 'var(--color-text-light)' }}>
                      <a href="mailto:contact@moov4goods.fr" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
                        contact@moov4goods.fr
                      </a>
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Clock size={24} style={{ color: 'var(--color-primary)', marginRight: 'var(--spacing-md)', flexShrink: 0, marginTop: '0.25rem' }} />
                  <div>
                    <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--color-text)' }}>
                      Horaires
                    </strong>
                    <p style={{ margin: 0, color: 'var(--color-text-light)', lineHeight: 'var(--line-height-relaxed)' }}>
                      Lundi - Vendredi<br />
                      9h00 - 18h00
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ 
                marginTop: 'var(--spacing-xl)', 
                padding: 'var(--spacing-lg)', 
                backgroundColor: 'rgba(15, 118, 110, 0.05)', 
                borderRadius: 'var(--border-radius-lg)',
                border: '1px solid var(--color-border)'
              }}>
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-light)', lineHeight: 'var(--line-height-relaxed)' }}>
                  <strong style={{ color: 'var(--color-primary)' }}>Note :</strong> Pour les demandes urgentes, 
                  n'hésitez pas à nous appeler directement. Nous nous efforçons de répondre à tous les messages 
                  dans un délai de 24 à 48 heures.
                </p>
              </div>
              </div>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Contact;
